// src/layout/AppShell.jsx
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { canSeeApprovals, isAdminRole } from "../auth/auth";
import UserProfileMenu from "../components/UserProfileMenu";
import "../styles/appshell.css";

function navClass({ isActive }) {
  return `topnav-link ${isActive ? "active" : ""}`;
}

function IconSpark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m12 3 1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="m19 15 .7 2.3L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.7L19 15Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function MoreItem({ to, children, onClick }) {
  return (
    <NavLink to={to} className="topnav-more-item" role="menuitem" onClick={onClick}>
      {children}
    </NavLink>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [moreOpen, setMoreOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const moreRef = useRef(null);
  const logoutCancelRef = useRef(null);
  const logoutTriggerRef = useRef(null);

  const admin = isAdminRole();
  const approvals = canSeeApprovals();
  const isLegalAiRoute = location.pathname.startsWith("/dashboard/ai/commentary");

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onPointerDown(event) {
      if (moreRef.current && !moreRef.current.contains(event.target)) {
        setMoreOpen(false);
      }
    }

    function onKeyDown(event) {
      if (event.key === "Escape") setMoreOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!showLogoutConfirm) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timer = window.setTimeout(() => logoutCancelRef.current?.focus?.({ preventScroll: true }), 0);

    function onKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowLogoutConfirm(false);
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      window.setTimeout(() => logoutTriggerRef.current?.focus?.({ preventScroll: true }), 0);
    };
  }, [showLogoutConfirm]);

  function handleLogout() {
    setShowLogoutConfirm(false);
    logout();
  }

  return (
    <div className="app-shell">
      <a className="la-skip-link" href="#main-content">Skip to main content</a>
      <header className="topnav">
        <div className="topnav-inner">
          <Link to="/dashboard" className="topnav-brand" aria-label="LawAfrica home">
            <img src="/logo.png" alt="LawAfrica" className="topnav-logo" />
            <span className="topnav-product">Digital Library</span>
          </Link>

          <nav className="topnav-links" aria-label="Main navigation">
            <NavLink to="/dashboard" end className={navClass}>
              Home
            </NavLink>
            <NavLink to="/dashboard/library" className={navClass}>
              Library
            </NavLink>
            <NavLink to="/dashboard/explore" className={navClass}>
              Explore
            </NavLink>
            <NavLink to="/dashboard/ai/commentary" className={navClass}>
              Legal AI
            </NavLink>

            {approvals && !admin ? (
              <NavLink to="/dashboard/approvals" className={navClass}>
                Institution
              </NavLink>
            ) : null}

            {admin ? (
              <NavLink to="/dashboard/admin" className={navClass}>
                Administration
              </NavLink>
            ) : null}

            {(admin || approvals) ? (
              <div className="topnav-more" ref={moreRef}>
                <button
                  type="button"
                  className={`topnav-link topnav-more-button ${moreOpen ? "active" : ""}`}
                  aria-haspopup="menu"
                  aria-expanded={moreOpen}
                  aria-controls={moreOpen ? "topnav-more-menu" : undefined}
                  onClick={() => setMoreOpen((value) => !value)}
                >
                  <span>More</span>
                  <span className="topnav-more-chevron" aria-hidden="true">⌄</span>
                </button>

                {moreOpen ? (
                  <div id="topnav-more-menu" className="topnav-more-menu" role="menu" aria-label="More navigation">
                    <MoreItem to="/dashboard/security" onClick={() => setMoreOpen(false)}>
                      Security
                    </MoreItem>
                  </div>
                ) : null}
              </div>
            ) : null}
          </nav>

          <div className="topnav-right">
            {user ? (
              <UserProfileMenu
                user={{
                  name: user.name,
                  email: user.email,
                  avatarUrl: user.avatarUrl,
                  profileImageUrl: user.profileImageUrl,
                  ProfileImageUrl: user.ProfileImageUrl,
                }}
                onLogout={() => { logoutTriggerRef.current = document.activeElement; setShowLogoutConfirm(true); }}
              />
            ) : null}
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className={`app-main ${isLegalAiRoute ? "app-main--legal-ai" : ""}`}>
        <div className={`app-main-inner ${isLegalAiRoute ? "app-main-inner--legal-ai" : ""}`}>
          <Outlet />
        </div>
      </main>

      {!isLegalAiRoute ? (
        <button
          type="button"
          className="la-ai-fab"
          title="Ask Legal AI"
          aria-label="Ask Legal AI"
          onClick={() => navigate("/dashboard/ai/commentary")}
        >
          <IconSpark />
          <span>Ask Legal AI</span>
        </button>
      ) : null}

      {showLogoutConfirm ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="logout-dialog-title" aria-describedby="logout-dialog-description">
          <div className="modal">
            <h3 id="logout-dialog-title">Sign out?</h3>
            <p id="logout-dialog-description">You will need to sign in again to access your LawAfrica account.</p>
            <div className="modal-actions">
              <button ref={logoutCancelRef} className="modal-btn secondary" type="button" onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </button>
              <button className="modal-btn danger" type="button" onClick={handleLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
